import { json, type LoaderFunctionArgs } from '@remix-run/node';
import type { ShouldRevalidateFunction } from '@remix-run/react';
import { z } from 'zod';
import {
    getConversationByID,
    getConversationByParticipants,
} from '~/modules/chat/api/get-conversation.server';
import { UPDATE_CONVERSATION_API } from './remix-api.chat.update-conversation';
import { UPDATE_CONVERSATION_THUMBNAIL_API } from './remix-api.chat.update-thumbnail';
import { ADD_PARTICIPANTS_API } from './remix-api.chat.add-participants';
import { REMOVE_PARTICIPANT_API } from './remix-api.chat.remove-participant';
import { INVITE_TO_GROUP_CHAT_API } from './remix-api.chat.invite-to-group-chat';
import { ARCHIVE_CONVERSATION_API } from './remix-api.chat.archive';
import { MUTE_CONVERSATION_API } from './remix-api.chat.mute';
import { MARK_CONVERSATION_READ_API } from './remix-api.chat.mark-conversation-read';
import { DELETE_MESSAGE_API } from './remix-api.chat.delete-message';
import { UPDATE_CONVERSATION_SETTINGS_API } from './remix-api.chat.update-settings';

export const CONVERSATION_API = '/remix-api/chat/conversation';

const ID_SCHEMA = z.coerce.number().int().positive();
const QUERY_SCHEMA = z
    .object({
        id: ID_SCHEMA.nullish(),
        participantUserIDs: z.array(ID_SCHEMA).nullish(),
        beforeID: ID_SCHEMA.nullish(),
    })
    .refine(
        (data) => {
            return !!data.id || !!data.participantUserIDs?.length;
        },
        {
            message: 'Either `id` or `participantUserIDs` must be provided',
        }
    );

/**
 * API endpoint to get a conversation between the user and another participant(s).
 * If passed both `id` and `participantUserIDs`, the `id` will be used.
 * Use with the `useConversation` hook.
 * @see {@link packages/web/app/modules/chat/hooks/use-conversation.ts}
 */
export const loader = async ({ context, request }: LoaderFunctionArgs) => {
    const { logger } = context;
    const url = new URL(request.url);
    const result = QUERY_SCHEMA.safeParse({
        id: url.searchParams.get('id'),
        participantUserIDs: url.searchParams.getAll('participantUserIDs'),
        beforeID: url.searchParams.get('beforeID'),
    });
    if (!result.success) {
        return json(null, { status: 400 });
    }

    try {
        const conversation = result.data.id
            ? await getConversationByID({
                  context,
                  id: result.data.id,
                  beforeID: result.data.beforeID ?? undefined,
              })
            : await getConversationByParticipants({
                  context,
                  participantUserIDs: result.data.participantUserIDs ?? [],
              });

        return json({ conversation });
    } catch (error) {
        logger.error(`Error in ${CONVERSATION_API}`, { error });
        return json(null, { status: 500 });
    }
};

export const shouldRevalidate: ShouldRevalidateFunction = ({
    defaultShouldRevalidate,
    formAction,
}) => {
    if (
        formAction === UPDATE_CONVERSATION_API ||
        formAction === UPDATE_CONVERSATION_THUMBNAIL_API ||
        formAction === ADD_PARTICIPANTS_API ||
        formAction === REMOVE_PARTICIPANT_API ||
        formAction === INVITE_TO_GROUP_CHAT_API ||
        formAction === ARCHIVE_CONVERSATION_API ||
        formAction === MUTE_CONVERSATION_API ||
        formAction === MARK_CONVERSATION_READ_API ||
        formAction === DELETE_MESSAGE_API ||
        formAction === UPDATE_CONVERSATION_SETTINGS_API
    ) {
        // Let the conversation cache update the conversation instead.
        return false;
    }
    return defaultShouldRevalidate;
};
